import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, TrendingDown, Minus, User, Target } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlayerPerformanceCard = ({ gameId, leagueId }) => {
  const [matchups, setMatchups] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (gameId) {
      fetchMatchups();
    }
  }, [gameId]);

  const fetchMatchups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/games/${gameId}/player-matchups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatchups(response.data);
    } catch (err) {
      console.error('Failed to load player matchups:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'better':
        return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'worse':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTrendBadge = (trend) => {
    switch (trend) {
      case 'better':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Better vs Opp</Badge>;
      case 'worse':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Worse vs Opp</Badge>;
      default:
        return <Badge variant="outline">Neutral</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <span className="text-sm text-muted-foreground">Loading player matchups...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!matchups || (!matchups.home_team_matchups?.length && !matchups.away_team_matchups?.length)) {
    return null;
  }

  const { stat_config, home_team_matchups, away_team_matchups } = matchups;
  const primaryStats = stat_config?.primary_stats || ['PTS', 'AST', 'REB'];
  const labels = stat_config?.labels || {};

  const renderPlayerCard = (player, isHome) => (
    <div 
      key={player.player_id} 
      className={`p-3 rounded-lg border ${
        isHome ? 'bg-green-500/5 border-green-500/20' : 'bg-blue-500/5 border-blue-500/20'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <User className={`w-4 h-4 ${isHome ? 'text-green-400' : 'text-blue-400'}`} />
          <span className="font-medium text-sm">{player.name}</span>
          {player.position && (
            <Badge variant="outline" className="text-xs">{player.position}</Badge>
          )}
        </div>
        {getTrendBadge(player.performance_vs_opponent)}
      </div>

      {player.vs_opponent_games > 0 && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="p-2 bg-card/50 rounded">
            <p className="text-muted-foreground mb-1">vs Opponent ({player.vs_opponent_games}g)</p>
            <div className="flex gap-3">
              {primaryStats.map(stat => (
                <div key={stat}>
                  <span className="text-muted-foreground">{labels[stat] || stat}: </span>
                  <span className="font-semibold">{player.vs_opponent_avg?.[stat] || '-'}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="p-2 bg-card/50 rounded">
            <p className="text-muted-foreground mb-1">Recent Form</p>
            <div className="flex gap-3">
              {primaryStats.map(stat => (
                <div key={stat}>
                  <span className="text-muted-foreground">{labels[stat] || stat}: </span>
                  <span className="font-semibold">{player.recent_avg?.[stat] || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {player.vs_opponent_games === 0 && (
        <p className="text-xs text-muted-foreground">No previous matchup data</p>
      )}
    </div>
  );

  return (
    <Card data-testid="player-performance-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="w-5 h-5" />
          Key Player Matchups
          <Badge variant="outline" className="ml-2 uppercase text-xs">{leagueId}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Home Team Players */}
        {home_team_matchups?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-green-400 mb-2 uppercase flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-400"></span>
              Home Team Key Players
            </p>
            <div className="space-y-2">
              {home_team_matchups.slice(0, 3).map(player => renderPlayerCard(player, true))}
            </div>
          </div>
        )}

        {/* Away Team Players */}
        {away_team_matchups?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-blue-400 mb-2 uppercase flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-400"></span>
              Away Team Key Players
            </p>
            <div className="space-y-2">
              {away_team_matchups.slice(0, 3).map(player => renderPlayerCard(player, false))}
            </div>
          </div>
        )}

        {/* Stats Legend */}
        <div className="pt-2 border-t border-border/30">
          <p className="text-xs text-muted-foreground">
            Stats shown: {primaryStats.map(s => labels[s] || s).join(', ')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlayerPerformanceCard;
